// Sorgente dei flow disponibili in pagina.
//
// Oggi i flow arrivano da `registerLocalFlow()` (definiti a codice); domani
// arriveranno dalla tabella su api-dev. L'unica cosa che cambia è il loader:
//
//   setFlowLoader(async () => (await apiService.get('/flows?status=published')).data)
//
// `WizardHost` non sa da dove vengono e non deve saperlo.
//
// Un flow che non supera `validateFlow()` NON entra mai nel registry: meglio
// che il bottone non compaia, che farlo comparire e piantarsi in mano
// all'operatore. L'errore resta in console e, in fase di pubblicazione, il
// builder lo mostra all'admin.

import { computed, ref, shallowRef, type ComputedRef } from 'vue'
import { validateFlow, type FlowDefinition } from './engine'

export type FlowLoader = () => Promise<FlowDefinition[]>

// `shallowRef` e non `ref`: `FlowCondition` è un tipo ricorsivo e l'unwrap
// profondo di Vue non converge (TS2589). È anche la semantica giusta — la
// lista si sostituisce sempre in blocco, non si muta in place — ed evita di
// rendere reattivo ogni nodo di ogni flow.
const flows = shallowRef<FlowDefinition[]>([])
const loading = ref(false)
const lastError = ref<string | null>(null)

let loader: FlowLoader | null = null
let loaded = false

/** Un flow è utilizzabile se pubblicato (o senza stato, come i seed locali). */
function isActive(flow: FlowDefinition): boolean {
  return flow.status === undefined || flow.status === 'published'
}

/** Scarta i flow non eseguibili, segnalando perché. */
function accept(flow: FlowDefinition, origin: string): boolean {
  const errors = validateFlow(flow).filter((i) => i.severity === 'error')
  if (errors.length === 0) return true

  console.error(
    `[flow] flow '${flow.id}' (${origin}) scartato: non è eseguibile`,
    errors.map((e) => `${e.path}: ${e.message}`),
  )

  // Causa di gran lunga più frequente, e la meno leggibile dal messaggio: il
  // flow usa un controllo o un'azione di DOMINIO, ma la pagina che monta il
  // <WizardHost> non ha importato il file che li registra. Il flow è a posto,
  // è la pagina a essere incompleta — e senza questa riga si finisce a cercare
  // l'errore nel flow, dove non c'è.
  const missing = [...new Set(
    errors
      .filter((issue) => /non registrat/.test(issue.message))
      .map((issue) => /'([^']+)'/.exec(issue.message)?.[1])
      .filter((name): name is string => Boolean(name)),
  )]
  if (missing.length > 0) {
    console.error(
      `[flow] '${flow.id}': ${missing.join(', ')} non risulta registrato in QUESTA pagina. `
      + "Quasi sempre manca l'import del registry di dominio nella pagina che monta il "
      + "<WizardHost>, per esempio: import '@/components/commessa/wizards/commessaFlowRegistry'",
    )
  }

  return false
}

/** Inserisce o aggiorna un flow. A parità di id vince la versione più alta. */
function upsert(flow: FlowDefinition): void {
  const index = flows.value.findIndex((f) => f.id === flow.id)
  if (index === -1) {
    flows.value = [...flows.value, flow]
    return
  }
  if ((flow.version ?? 0) < (flows.value[index].version ?? 0)) return

  const next = [...flows.value]
  next[index] = flow
  flows.value = next
}

/** Registra un flow definito a codice (seed, sviluppo, anteprima del builder). */
export function registerLocalFlow(flow: FlowDefinition): void {
  if (!accept(flow, 'locale')) return
  upsert(flow)
}

export function setFlowLoader(fn: FlowLoader | null): void {
  loader = fn
  loaded = false
}

/** Carica i flow dalla sorgente remota, una volta sola (o su `force`). */
export async function ensureFlowsLoaded(force = false): Promise<void> {
  if (!loader) return
  if (loaded && !force) return

  loading.value = true
  lastError.value = null
  try {
    for (const flow of await loader()) {
      if (accept(flow, 'remoto')) upsert(flow)
    }
    loaded = true
  } catch (e) {
    lastError.value = e instanceof Error ? e.message : String(e)
    console.error('[flow] caricamento flow fallito:', e)
  } finally {
    loading.value = false
  }
}

/** Flow pubblicati agganciati a un punto di innesto. */
export function flowsForAnchor(anchor: string): FlowDefinition[] {
  return flows.value.filter((f) => f.anchor === anchor && isActive(f))
}

export function useFlowSource(): {
  flows: ComputedRef<FlowDefinition[]>
  loading: ComputedRef<boolean>
  lastError: ComputedRef<string | null>
} {
  return {
    flows: computed(() => flows.value),
    loading: computed(() => loading.value),
    lastError: computed(() => lastError.value),
  }
}
